import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { materialCost as eur, num } from "@/lib/format";

const Cost = ({ v, unit }) => (v > 0 ? <>{eur(v)}{unit}</> : <span className="text-xs text-amber-400">manca prezzo</span>);
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, Minus, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/materials/ConfirmDelete";
import { SeasonSelect } from "@/components/materials/SeasonSelect";
import { FuseControl } from "@/components/materials/FuseControl";
import { ZipStock } from "@/components/materials/ZipStock";
import { isZip } from "@/lib/zips";

const STEP = 10;

// Aggiorna subito la riga a schermo, poi salva in background
const useQuantity = (m) => {
  const qc = useQueryClient();
  const setLocal = (q) => qc.setQueryData(["materials"], (list) => list?.map((x) => (x.id === m.id ? { ...x, quantity: q } : x)));
  return useMutation({
    mutationFn: async (q) => (await api.patch(`/materials/${m.id}`, { quantity: q })).data,
    onMutate: (q) => { const prev = m.quantity; setLocal(q); return { prev }; },
    onError: (_e, _q, ctx) => { setLocal(ctx.prev); toast.error("Quantità non salvata"); },
  });
};

const QtyEditor = ({ m, onSave, low }) => {
  const [edit, setEdit] = useState(null);
  const commit = () => {
    const v = (edit ?? "").trim().replace(",", ".");
    setEdit(null);
    if (!v) return;
    // "+25" / "-5" = carico/scarico, "120" = nuova giacenza
    const q = /^[+-]/.test(v) ? (m.quantity || 0) + Number(v) : Number(v);
    if (Number.isFinite(q) && q !== m.quantity) onSave(Math.max(0, q));
  };
  if (edit !== null) {
    return <Input autoFocus value={edit} onChange={(e) => setEdit(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEdit(null); }}
      className="mx-auto h-8 w-28 text-center" placeholder="es. 120 o +25" data-testid={`mat-qty-input-${m.id}`} />;
  }
  return (
    <button type="button" onClick={() => setEdit(String(m.quantity ?? 0))} title="Clicca per scrivere la quantità (es. 120, +25, -5)"
      className={`rounded px-2 py-1 font-semibold underline decoration-dotted underline-offset-4 transition-colors hover:bg-secondary ${low ? "text-rose-400" : ""}`} data-testid={`mat-qty-${m.id}`}>
      {num(m.quantity)} {m.unit}
    </button>
  );
};

const SeasonCell = ({ m, seasons }) => {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: async (season) => (await api.patch(`/materials/${m.id}`, { season })).data,
    onSuccess: (_d, s) => { qc.invalidateQueries({ queryKey: ["materials"] }); toast.success(`Spostato nella stagione ${s}`); },
  });
  return <SeasonSelect compact withAll={false} value={m.season} options={seasons} onChange={(s) => save.mutate(s)} testid={`mat-season-${m.id}`} />;
};

const EnableFuse = ({ m }) => {
  const qc = useQueryClient();
  const on = useMutation({
    mutationFn: async () => (await api.patch(`/materials/${m.id}`, { fusible: true })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["materials"] }); toast.success("Tessuto segnato come adesivabile"); },
  });
  return (
    <button type="button" onClick={() => on.mutate()} className="mt-1 w-full text-[11px] text-muted-foreground hover:text-foreground" title="Questo tessuto non è segnato come adesivabile" data-testid={`mat-enable-fuse-${m.id}`}>
      Non adesivabile · <span className="underline decoration-dotted underline-offset-2">attiva</span>
    </button>
  );
};

const VariantRow = ({ m, cheapest, fabric, seasons }) => {
  const save = useQuantity(m);
  const step = (d) => save.mutate(Math.max(0, (m.quantity || 0) + d));
  const low = m.quantity <= 0;
  const zip = !fabric && isZip(m);
  return (
    <TableRow className="bg-secondary/20" data-testid={`material-row-${m.id}`}>
      <TableCell className="pl-10 text-sm">
        <div className="flex flex-wrap items-center gap-2">{m.color || "-"}{fabric && seasons && <SeasonCell m={m} seasons={seasons} />}</div>
        {m.covered_fabric_name && <span className="block text-[11px] text-muted-foreground" data-testid={`mat-cover-${m.id}`}>ricoperto con {m.covered_fabric_name}{m.covered_fabric_supplier ? ` - ${m.covered_fabric_supplier}` : ""}</span>}
      </TableCell>
      <TableCell className="text-sm">
        {m.supplier || "-"}{cheapest && <Badge variant="outline" className="ml-2 border-emerald-500/50 text-[10px] text-emerald-400">più conveniente</Badge>}
        {m.supplier_code && <span className="block text-[11px] text-muted-foreground">cod. colore {m.supplier_code}</span>}
      </TableCell>
      <TableCell className="text-right text-sm"><Cost v={m.cost_per_unit} unit={fabric ? "/m" : ""} /></TableCell>
      <TableCell className="text-center">
        {zip ? <ZipStock m={m} /> : <QtyEditor m={m} low={low} onSave={(q) => save.mutate(q)} />}
        {low && <p className="text-[11px] text-rose-400">esaurito</p>}
        {fabric && (m.fusible || m.fused > 0 || m.to_fuse > 0) && <FuseControl m={m} />}
        {fabric && !(m.fusible || m.fused > 0 || m.to_fuse > 0) && <EnableFuse m={m} />}
        {fabric && m.fused > 0 && (
          <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`mat-split-${m.id}`}>
            Normale <b className="text-foreground">{num(Math.max(0, (m.quantity || 0) - m.fused))} m</b> · Adesivato <b className="text-sky-400">{num(Math.min(m.fused, m.quantity || 0))} m</b>
          </p>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-center gap-1">
          {!zip && <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => step(-STEP)} data-testid={`mat-dec-${m.id}`}><Minus className="h-3.5 w-3.5" /></Button>}
          {!zip && <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => step(STEP)} data-testid={`mat-inc-${m.id}`}><Plus className="h-3.5 w-3.5" /></Button>}
          <ConfirmDelete title="Eliminare questa variante?" description={`${m.name} · ${m.color || "-"} · ${m.supplier || "-"} (${num(m.quantity)} ${m.unit}).`}
            url={`/materials/${m.id}`} queryKey={["materials"]} success="Variante eliminata" testid={`mat-delete-${m.id}`} />
        </div>
      </TableCell>
    </TableRow>
  );
};

const cheapestIds = (variants) => {
  // Per ogni colore con più fornitori, evidenzia il prezzo più basso
  const byColor = {};
  variants.forEach((v) => { (byColor[(v.color || "").toLowerCase()] ||= []).push(v); });
  return new Set(Object.values(byColor).filter((l) => l.length > 1 && new Set(l.map((v) => v.supplier)).size > 1)
    .map((l) => l.reduce((a, b) => (b.cost_per_unit < a.cost_per_unit ? b : a)).id));
};

export const MaterialGroup = ({ group, fabric, kind, defaultOpen = false, onAddVariant, onEdit, seasons, showSeason }) => {
  const [open, setOpen] = useState(defaultOpen);
  const { name, variants } = group;
  const first = variants[0];
  const colors = new Set(variants.map((v) => v.color).filter(Boolean));
  const suppliers = new Set(variants.map((v) => v.supplier).filter(Boolean));
  const lowCount = variants.filter((v) => v.quantity <= 0).length;
  const total = variants.reduce((s, v) => s + (v.quantity || 0), 0);
  const toFuse = variants.reduce((s, v) => s + (v.to_fuse || 0), 0);
  const fused = variants.reduce((s, v) => s + (v.fused || 0), 0);
  const costs = variants.map((v) => v.cost_per_unit || 0);
  const cheap = cheapestIds(variants);
  const slug = `${name}${showSeason && first.season ? `-${first.season}` : ""}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setOpen(!open)} data-testid={`material-group-${slug}`}>
        <TableCell className="font-medium">
          <span className="flex items-center gap-1.5">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{name}{showSeason && first.season && <Badge variant="outline" className="ml-1 text-[10px]" data-testid={`material-group-season-${slug}`}>{first.season}</Badge>}</span>
          <span className="ml-6 block text-[11px] font-normal text-muted-foreground">
            {fabric ? first.composition || "-" : <span className="capitalize">{first.type}</span>}
          </span>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">{colors.size} {colors.size === 1 ? "colore" : "colori"} · {suppliers.size} {suppliers.size === 1 ? "fornitore" : "fornitori"}</TableCell>
        <TableCell className="text-right text-sm">{Math.min(...costs) === Math.max(...costs) ? <Cost v={costs[0]} unit="" /> : `${eur(Math.min(...costs))} – ${eur(Math.max(...costs))}`}</TableCell>
        <TableCell className="text-center">
          <span className="font-semibold">{num(total)} {first.unit}</span>
          {lowCount > 0 && <p className="text-[11px] text-rose-400">{lowCount} {lowCount === 1 ? "esaurita" : "esaurite"}</p>}
          {fabric && toFuse > 0 && <p className="text-[11px] text-amber-400" data-testid={`material-group-to-fuse-${slug}`}>{num(toFuse)} m da adesivare</p>}
          {fabric && fused > 0 && <p className="text-[11px] text-sky-400">{num(fused)} m adesivati</p>}
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => onAddVariant(group)} data-testid={`material-add-variant-${slug}`}><Plus className="mr-1 h-3.5 w-3.5" />Variante</Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(group)} data-testid={`material-edit-${slug}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <ConfirmDelete title={`Eliminare "${name}"?`} description={`Verranno eliminate tutte le ${variants.length} varianti (colori e fornitori)${fabric && first.season ? ` della stagione ${first.season}` : ""}.`}
              url="/materials/group" params={{ name, kind, ...(fabric && first.season ? { season: first.season } : {}) }} queryKey={["materials"]} success="Materiale eliminato" testid={`material-delete-${slug}`} />
          </div>
        </TableCell>
      </TableRow>
      {open && variants.map((m) => <VariantRow key={m.id} m={m} fabric={fabric} seasons={seasons} cheapest={cheap.has(m.id)} />)}
    </>
  );
};
